import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Stack,
  Chip,
  Divider,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PersonGraph from '../../components/PersonGraph/PersonGraph';

export interface ShareholdingItem {
  company: { id?: string; label?: string; jurisdiction?: string };
  edge: {
    id?: string;
    label?: string;
    data?: { sharesHeld?: number; ownershipType?: string };
  };
}

interface PersonShareholderSectionProps {
  shareholdings: ShareholdingItem[];
  view: 'list' | 'graph';
  onViewChange: (view: 'list' | 'graph') => void;
  personId: string | undefined;
  showViewToggle?: boolean;
}

const PersonShareholderSection: React.FC<PersonShareholderSectionProps> = ({
  shareholdings,
  view,
  onViewChange,
  personId,
  showViewToggle = false,
}) => {
  const renderList = () => (
    <Card>
      <CardContent>
        {!showViewToggle && (
          <>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              Shareholdings
              <Chip label={shareholdings.length} size="small" color="success" />
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </>
        )}
        {shareholdings.length === 0 ? (
          <Alert severity="info">No shareholdings found.</Alert>
        ) : (
          <List disablePadding={!showViewToggle}>
            {shareholdings.map(({ company, edge }, idx: number) => {
              const percentageLabel =
                edge?.label && String(edge.label).includes('%') ? edge.label : '';
              const sharesHeld = edge?.data?.sharesHeld
                ? Number(edge.data.sharesHeld).toLocaleString()
                : edge?.label && !String(edge.label).includes('%')
                  ? edge.label
                  : '';
              const percentMatch = percentageLabel
                ? String(percentageLabel).match(/(\d+(?:\.\d+)?)/)
                : null;
              const percentage = percentMatch ? parseFloat(percentMatch[1]) : null;
              let chipColor = '#9e9e9e';
              let levelLabel = '';
              if (percentage !== null) {
                if (percentage >= 50) {
                  chipColor = '#d32f2f';
                  levelLabel = 'Majority';
                } else if (percentage >= 25) {
                  chipColor = '#f57c00';
                  levelLabel = 'Significant';
                } else if (percentage >= 10) {
                  chipColor = '#1976d2';
                  levelLabel = 'Moderate';
                } else if (percentage > 0) {
                  chipColor = '#388e3c';
                  levelLabel = 'Minor';
                }
              } else if (sharesHeld) {
                chipColor = '#757575';
                levelLabel = 'Shareholder';
              }
              const ownershipType = edge?.data?.ownershipType || 'DIRECT';

              return (
                <React.Fragment key={edge.id ?? idx}>
                  {idx > 0 && <Divider />}
                  <ListItemButton
                    component={RouterLink}
                    to={`/companies/${encodeURIComponent(company?.id ?? '')}`}
                    sx={showViewToggle ? {} : { px: 0 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight="medium" component="span">
                            {company?.label}
                          </Typography>
                          {percentageLabel && (
                            <Chip
                              label={percentageLabel}
                              size="small"
                              sx={{
                                bgcolor: chipColor,
                                color: 'white',
                                fontWeight: 'bold',
                                ml: 1,
                              }}
                            />
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }} component="span">
                            {company?.jurisdiction || 'Unknown jurisdiction'}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                            {levelLabel && (
                              <Chip
                                label={levelLabel}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: chipColor,
                                  color: chipColor,
                                  bgcolor: `${chipColor}0d`,
                                  fontWeight: 500,
                                }}
                              />
                            )}
                            {ownershipType && (
                              <Chip
                                label={ownershipType}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                            )}
                            {sharesHeld && (
                              <Chip
                                label={`${sharesHeld} shares`}
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            )}
                          </Stack>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {(showViewToggle && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Shareholdings ({shareholdings.length})</Typography>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, newView) => newView && onViewChange(newView)}
            size="small"
          >
            <ToggleButton value="list">
              <ViewListIcon fontSize="small" sx={{ mr: 1 }} />
              List
            </ToggleButton>
            <ToggleButton value="graph">
              <AccountTreeIcon fontSize="small" sx={{ mr: 1 }} />
              Graph
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )) || (
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          Shareholdings
          <Chip label={shareholdings.length} size="small" color="success" />
        </Typography>
      )}

      {view === 'graph' && personId ? (
        <PersonGraph personId={personId} view="shareholdings" height="700px" />
      ) : (
        renderList()
      )}
    </Box>
  );
};

export default PersonShareholderSection;
